import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { X, Pencil, Plus, Loader2, Handshake, Power, KeyRound, FileText } from "lucide-react";

const EMPTY = { partner_name: "", email: "", password: "", commission_percent: 10, language: "es", phone: "", monthly_goal: "", goal_bonus_percent: "" };
const LANGS = [["es", "ES · Español"], ["en", "EN · English"], ["de", "DE · Deutsch"], ["it", "IT · Italiano"]];

export default function PartnersTab({ headers, canEdit }) {
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/partners`, { headers }).then(({ data }) => setPartners(data.partners)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const create = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/partners`, form, { headers });
      toast.success(`Partner ${form.partner_name} creado`);
      setForm(EMPTY);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d === "email_exists" ? "Ese email ya existe" : d === "weak_password" ? "Contraseña de mínimo 8 caracteres" : "Error al crear");
    } finally { setBusy(false); }
  };

  const downloadStatement = async (p) => {
    try {
      const res = await axios.get(`${API}/admin/partners/${p.partner_code}/statement`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `informe-${p.partner_code.toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Informe de ${p.partner_name} descargado`);
    } catch { toast.error("No se pudo generar el informe"); }
  };

  const toggleActive = async (p) => {
    await axios.patch(`${API}/admin/partners/${p.partner_code}`, { active: !p.active }, { headers });
    toast.success(p.active ? `${p.partner_name} desactivado` : `${p.partner_name} activado`);
    load();
  };

  return (
    <div data-testid="partners-tab">
      <h2 className="font-display text-xl font-bold text-topacio-ink mb-2">Partners y comisiones</h2>
      <p className="text-xs text-topacio-faded font-semibold mb-6">Cada partner con su % de comisión. Las liquidaciones (fecha y método) se marcan en cada reserva de la pestaña Reservas.</p>

      {canEdit && (
        <div className="card-brutal p-6 mb-8">
          <h3 className="font-display text-base font-bold text-topacio-ink mb-5">Alta de partner</h3>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Nombre (hotel, empresa…)</label>
              <input data-testid="partner-name-input" value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} className="input-brutal mt-1 !w-52" />
            </div>
            <div>
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Email</label>
              <input data-testid="partner-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-brutal mt-1 !w-56" />
            </div>
            <div>
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Contraseña</label>
              <input data-testid="partner-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="mín. 8 caracteres" className="input-brutal mt-1 !w-40" />
            </div>
            <div>
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">% comisión</label>
              <input data-testid="partner-commission-input" type="number" min="0" max="50" step="0.5" value={form.commission_percent}
                onChange={(e) => setForm({ ...form, commission_percent: parseFloat(e.target.value) || 0 })} className="input-brutal mt-1 !w-28" />
            </div>
            <div>
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Idioma emails</label>
              <select data-testid="partner-language-select" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="input-brutal mt-1 !w-40">
                {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">WhatsApp (opcional)</label>
              <input data-testid="partner-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+34 600 000 000" className="input-brutal mt-1 !w-44" />
            </div>
            <div>
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Meta mensual</label>
              <input data-testid="partner-goal-input" type="number" min="0" max="200" value={form.monthly_goal}
                onChange={(e) => setForm({ ...form, monthly_goal: e.target.value ? parseInt(e.target.value) : "" })} placeholder="reservas/mes" className="input-brutal mt-1 !w-32" />
            </div>
            <div>
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">% bonus por meta</label>
              <input data-testid="partner-bonus-input" type="number" min="0" max="20" step="0.5" value={form.goal_bonus_percent}
                onChange={(e) => setForm({ ...form, goal_bonus_percent: e.target.value ? parseFloat(e.target.value) : "" })} placeholder="+%" className="input-brutal mt-1 !w-28" />
            </div>
            <button data-testid="partner-create-btn" onClick={create} disabled={busy || !form.partner_name || !form.email || !form.password} className="btn-primary !px-5 !py-3 !text-xs">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crear partner
            </button>
          </div>
          <p className="text-[11px] text-topacio-faded font-semibold mt-3">Entra desde /admin/login con su email y va directo a su portal de reservas.</p>
        </div>
      )}

      <div className="card-brutal overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead>
            <tr className="bg-topacio-ink text-left text-xs uppercase tracking-wider text-white">
              {["Partner", "Código", "% comisión", "Reservas", "Pendiente", "Cobrado", "Estado", ""].map((h) => (
                <th key={h} className="px-5 py-4 font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {partners.map((p) => (
              <tr key={p.partner_code} data-testid={`partner-row-${p.partner_code}`} className={`hover:bg-topacio-sand transition-colors ${p.active === false ? "opacity-50" : ""}`}>
                <td className="px-5 py-4">
                  <div className="font-bold text-topacio-ink flex items-center gap-2"><Handshake className="w-4 h-4 text-topacio-red" />{p.partner_name}</div>
                  <div className="text-xs text-topacio-faded">{p.email} · {(p.language || "es").toUpperCase()}</div>
                </td>
                <td className="px-5 py-4 font-mono text-xs font-extrabold text-topacio-ink">{p.partner_code}</td>
                <td className="px-5 py-4 font-extrabold text-topacio-red">{p.commission_percent}%
                  {p.goal_bonus_percent > 0 && <div className="text-[10px] text-topacio-green font-extrabold">+{p.goal_bonus_percent}% si supera meta</div>}
                </td>
                <td className="px-5 py-4">{p.bookings_count}</td>
                <td className="px-5 py-4 font-extrabold text-topacio-ink">{p.commission_pending ?? 0}€</td>
                <td className="px-5 py-4 text-topacio-green font-bold">{p.commission_paid_total ?? 0}€</td>
                <td className="px-5 py-4">
                  <span className={`badge-brutal ${p.active !== false ? "bg-topacio-green text-white" : "bg-zinc-300 text-topacio-faded"}`}>
                    {p.active !== false ? "Activo" : "Inactivo"}
                  </span>
                </td>
                <td className="px-5 py-4">
                  {canEdit && (
                    <div className="flex gap-2">
                      <button data-testid={`partner-statement-${p.partner_code}`} onClick={() => downloadStatement(p)} title="Informe mensual PDF (mes pasado)"
                        className="w-8 h-8 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-brand transition-colors">
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`partner-edit-${p.partner_code}`} onClick={() => setEditing({ ...p, newPassword: "" })} title="Editar"
                        className="w-8 h-8 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-brand transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`partner-toggle-${p.partner_code}`} onClick={() => toggleActive(p)} title={p.active !== false ? "Desactivar acceso" : "Activar acceso"}
                        className="w-8 h-8 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-topacio-red hover:text-white transition-colors">
                        <Power className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {partners.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-12 text-center text-topacio-faded">Sin partners todavía. Da de alta el primero arriba.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="partner-editor-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setEditing(null)} />
          <div className="relative card-brutal w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-topacio-ink">Editar {editing.partner_name}</h3>
              <button data-testid="partner-editor-close" onClick={() => setEditing(null)} className="text-topacio-faded hover:text-topacio-ink"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Nombre</label>
                <input data-testid="partner-edit-name" value={editing.partner_name} onChange={(e) => setEditing({ ...editing, partner_name: e.target.value })} className="input-brutal mt-1" />
              </div>
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">% comisión</label>
                <input data-testid="partner-edit-commission" type="number" min="0" max="50" step="0.5" value={editing.commission_percent}
                  onChange={(e) => setEditing({ ...editing, commission_percent: parseFloat(e.target.value) || 0 })} className="input-brutal mt-1 !w-32" />
                <p className="text-[10px] text-topacio-faded font-semibold mt-1">Se aplica a las reservas NUEVAS que creen desde su portal.</p>
              </div>
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Idioma de sus emails</label>
                <select data-testid="partner-edit-language" value={editing.language || "es"} onChange={(e) => setEditing({ ...editing, language: e.target.value })} className="input-brutal mt-1 !w-48">
                  {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <p className="text-[10px] text-topacio-faded font-semibold mt-1">Recibirá el aviso de cada reserva con su código en este idioma.</p>
              </div>
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Meta mensual de reservas</label>
                <input data-testid="partner-edit-goal" type="number" min="0" max="200" value={editing.monthly_goal || ""}
                  onChange={(e) => setEditing({ ...editing, monthly_goal: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Vacío = sin meta" className="input-brutal mt-1 !w-40" />
                <p className="text-[10px] text-topacio-faded font-semibold mt-1">Verá una barra de progreso en su portal con las reservas del mes.</p>
              </div>
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">% bonus al superar la meta</label>
                <input data-testid="partner-edit-bonus" type="number" min="0" max="20" step="0.5" value={editing.goal_bonus_percent || ""}
                  onChange={(e) => setEditing({ ...editing, goal_bonus_percent: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="0 = sin bonus" className="input-brutal mt-1 !w-40" />
                <p className="text-[10px] text-topacio-faded font-semibold mt-1">Se suma a su comisión en las reservas que haga tras alcanzar la meta ese mes.</p>
              </div>
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">WhatsApp del partner</label>
                <input data-testid="partner-edit-phone" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                  placeholder="+34 600 000 000 (vacío = sin avisos WhatsApp)" className="input-brutal mt-1" />
                <p className="text-[10px] text-topacio-faded font-semibold mt-1">Recibirá un WhatsApp inmediato con cada reserva hecha con su código.</p>
              </div>
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Nueva contraseña (opcional)</label>
                <input data-testid="partner-edit-password" type="text" value={editing.newPassword} onChange={(e) => setEditing({ ...editing, newPassword: e.target.value })}
                  placeholder="Dejar vacío para no cambiarla" className="input-brutal mt-1" />
              </div>
              <button data-testid="partner-editor-save" disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const payload = { partner_name: editing.partner_name, commission_percent: editing.commission_percent, language: editing.language || "es", phone: editing.phone || "", monthly_goal: editing.monthly_goal || 0, goal_bonus_percent: editing.goal_bonus_percent || 0 };
                    if (editing.newPassword) payload.password = editing.newPassword;
                    await axios.patch(`${API}/admin/partners/${editing.partner_code}`, payload, { headers });
                    toast.success("Partner actualizado");
                    setEditing(null);
                    load();
                  } catch { toast.error("Error al guardar"); } finally { setBusy(false); }
                }}
                className="btn-primary w-full !py-3">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
